import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import {
    alpha,
    Box,
    Link,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../layout/styledcomponents/CustomButton';
import UserRole from '../../shared/AuthRoles';

const ERROR_PAGE_IMAGE = '/illustrations/permission-denied.png';

export default function ErrorPage() {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const navigate = useNavigate();
    const theme = useTheme();
    const loggedInUser = UserRole();
    const isOwnerOrAdmin =
        Boolean(loggedInUser?.role?.includes('owner')) ||
        loggedInUser?.role === 'admin';
    const usesSidebarNav = isOwnerOrAdmin && !isMobile && !isIpad;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <Box
            sx={{
                position: 'relative',
                mt: usesSidebarNav ? '-130px' : 0,
                minHeight: usesSidebarNav
                    ? 'var(--app-viewport-height)'
                    : isMobile
                      ? 'calc(var(--app-viewport-height) - 180px)'
                      : 'calc(var(--app-viewport-height) - 130px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 1, sm: 2 },
                py: { xs: 2, sm: 3 },
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: '100%',
                    maxWidth: 520,
                    px: { xs: 3, sm: 5 },
                    py: { xs: 4, sm: 5 },
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                    boxShadow: '0px 12px 32px rgba(20, 55, 74, 0.08)',
                    textAlign: 'center',
                }}
            >
                <Stack spacing={2.5} alignItems='center'>
                    <Box
                        component='img'
                        src={ERROR_PAGE_IMAGE}
                        alt=''
                        sx={{
                            width: '100%',
                            maxWidth: { xs: 280, sm: 340 },
                            height: 'auto',
                            display: 'block',
                        }}
                    />

                    <Stack spacing={1.25} alignItems='center'>
                        <Typography
                            component='h1'
                            sx={{
                                fontFamily: theme.typography.h2.fontFamily,
                                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                fontWeight: 700,
                                color: theme.palette.primary.dark,
                                lineHeight: 1.2,
                            }}
                        >
                            404
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: { xs: '0.875rem', sm: '0.95rem' },
                                color: theme.palette.text.secondary,
                                maxWidth: 420,
                                lineHeight: 1.6,
                            }}
                        >
                            The page you are looking for might have been
                            removed, had it&apos;s name changed or is
                            temporarily unavailable.
                        </Typography>
                    </Stack>

                    <Stack spacing={1.5} alignItems='center' sx={{ pt: 0.5 }}>
                        <CustomButton
                            variant='contained'
                            color='primary'
                            startIcon={<HomeOutlinedIcon />}
                            onClick={() => navigate('/dashboard')}
                            sx={{
                                minWidth: 220,
                                px: 3,
                                py: 1.25,
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                borderRadius:
                                    theme.palette.custom.button.borderRadius,
                            }}
                        >
                            Go to dashboard
                        </CustomButton>

                        <Link
                            component='button'
                            type='button'
                            underline='none'
                            onClick={() => navigate(-1)}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: theme.palette.textAccent.main,
                                fontSize: '0.95rem',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                    color: theme.palette.info.main,
                                },
                            }}
                        >
                            ← Go Back
                        </Link>
                    </Stack>
                </Stack>
            </Paper>
        </Box>
    );
}
